import { useState } from "react";
import { CheckCircle2, FileUp, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { formatApiError, postBourseDemande, uploadBourseDocument } from "@/lib/api";

const DOCUMENTS = [
  { key: "plan_affaires", label: "Plan d'affaires", obligatoire: true },
  { key: "etats_financiers", label: "États financiers", obligatoire: true },
  { key: "plan_previsionnel", label: "Plan prévisionnel sur deux ans", obligatoire: true },
  { key: "organigramme", label: "Organigramme", obligatoire: true },
  { key: "chiffre_affaires", label: "Chiffre d'affaires déjà réalisé, si applicable", obligatoire: false },
];

const FORM_INITIAL = {
  type_demandeur: "personne",
  prenom: "",
  nom: "",
  courriel: "",
  telephone: "",
  organisation: "",
  titre_projet: "",
  description_projet: "",
  besoin_financier: "",
  montant_demande: "",
  impact_attendu: "",
  utilisation_fonds: "",
  chiffre_affaires_realise: "",
  consentement: false,
};

const REPRESENTANT_INITIAL = { prenom: "", nom: "", titre: "", courriel: "", telephone: "" };
const MEMBRE_INITIAL = { prenom: "", nom: "", role: "", date_naissance: "", piece_identite: null };

const inputCls = "w-full rounded-sm border border-border bg-background px-4 py-3 text-sm outline-none transition-colors focus:border-primary";
const labelCls = "text-xs font-semibold uppercase tracking-[0.15em] text-foreground/50";

const DocumentUpload = ({ doc, valeur, chargement, onChange }) => (
  <div className="border border-border bg-background p-5" data-testid={`bourse-document-${doc.key}`}>
    <p className="text-sm font-semibold">
      {doc.label} {doc.obligatoire ? <span className="text-primary">*</span> : <span className="text-foreground/45">(optionnel)</span>}
    </p>
    <label className="mt-4 flex cursor-pointer items-center justify-center gap-3 border border-dashed border-foreground/25 px-4 py-5 text-sm text-foreground/55 transition-colors hover:border-primary hover:text-primary">
      {chargement ? <Loader2 size={17} className="animate-spin" /> : valeur ? <CheckCircle2 size={17} className="text-primary" /> : <FileUp size={17} />}
      <span className="max-w-full truncate">{valeur ? valeur.nom_original : "Choisir un fichier"}</span>
      <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp" className="sr-only" onChange={(e) => onChange(doc.key, e.target.files?.[0])} data-testid={`bourse-file-${doc.key}`} />
    </label>
    <p className="mt-2 text-xs text-foreground/45">PDF, Word, Excel ou image — 20 Mo maximum.</p>
  </div>
);

const PieceIdentiteUpload = ({ membre, index, chargement, onChange }) => (
  <label className="flex cursor-pointer items-center justify-center gap-3 border border-dashed border-foreground/25 px-4 py-4 text-sm text-foreground/55 transition-colors hover:border-primary hover:text-primary md:col-span-2">
    {chargement ? <Loader2 size={17} className="animate-spin" /> : membre.piece_identite ? <CheckCircle2 size={17} className="text-primary" /> : <FileUp size={17} />}
    <span className="max-w-full truncate">{membre.piece_identite ? membre.piece_identite.nom_original : "Pièce d'identité gouvernementale *"}</span>
    <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="sr-only" onChange={(e) => onChange(index, e.target.files?.[0])} data-testid={`bourse-piece-identite-${index}`} />
  </label>
);

export const BourseApplication = ({ campagne }) => {
  const [form, setForm] = useState(FORM_INITIAL);
  const [representant, setRepresentant] = useState(REPRESENTANT_INITIAL);
  const [membres, setMembres] = useState([{ ...MEMBRE_INITIAL }]);
  const [documents, setDocuments] = useState({});
  const [chargementDocument, setChargementDocument] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const estOrganisation = form.type_demandeur === "obnl" || form.type_demandeur === "societe";
  const nomOrganisation = form.type_demandeur === "societe" ? "Nom de la société par actions *" : "Nom de l'OBNL *";
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const setRepresentantField = (key) => (e) => setRepresentant((r) => ({ ...r, [key]: e.target.value }));
  const setMembreField = (index, key, value) => setMembres((items) => items.map((item, i) => (i === index ? { ...item, [key]: value } : item)));

  const choisirDocument = async (type, file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error("Chaque document doit faire au maximum 20 Mo.");
      return;
    }
    setChargementDocument(type);
    try {
      const doc = await uploadBourseDocument(type, file);
      setDocuments((d) => ({ ...d, [type]: doc }));
      toast.success(`${doc.nom_original} téléversé`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setChargementDocument(null);
    }
  };

  const choisirPieceIdentite = async (index, file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error("Chaque pièce d'identité doit faire au maximum 20 Mo.");
      return;
    }
    setChargementDocument(`piece-${index}`);
    try {
      const doc = await uploadBourseDocument("piece_identite", file);
      setMembreField(index, "piece_identite", doc);
      toast.success("Pièce d'identité téléversée");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setChargementDocument(null);
    }
  };

  const soumettre = async (e) => {
    e.preventDefault();
    const manquants = DOCUMENTS.filter((d) => d.obligatoire && !documents[d.key]);
    if (manquants.length) {
      toast.error(`Documents manquants : ${manquants.map((d) => d.label).join(", ")}`);
      return;
    }
    if (estOrganisation) {
      if (Object.values(representant).some((v) => !v)) {
        toast.error("Veuillez compléter les coordonnées du représentant autorisé.");
        return;
      }
      const membreIncomplet = membres.some((m) => !m.prenom || !m.nom || !m.role || !m.date_naissance || !m.piece_identite);
      if (membreIncomplet) {
        toast.error("Chaque membre du CA doit avoir un nom, un rôle, une date de naissance et une pièce d'identité.");
        return;
      }
    }
    if (!form.consentement) {
      toast.error("Veuillez consentir à l'analyse de votre demande.");
      return;
    }
    setEnvoi(true);
    try {
      const payload = {
        ...form,
        organisation: estOrganisation ? form.organisation : null,
        representant: estOrganisation ? representant : null,
        membres_ca: estOrganisation ? membres.map((m) => ({
          prenom: m.prenom,
          nom: m.nom,
          role: m.role,
          date_naissance: m.date_naissance,
          piece_identite_id: m.piece_identite.document_id,
        })) : [],
        montant_demande: Number(form.montant_demande),
        chiffre_affaires_realise: form.chiffre_affaires_realise || null,
        documents: Object.fromEntries(Object.entries(documents).map(([key, doc]) => [key, doc.document_id])),
      };
      const res = await postBourseDemande(payload);
      setConfirmation(res.message);
      setForm(FORM_INITIAL);
      setRepresentant(REPRESENTANT_INITIAL);
      setMembres([{ ...MEMBRE_INITIAL }]);
      setDocuments({});
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <form onSubmit={soumettre} className="mt-10 space-y-10 bg-background p-6 text-foreground md:p-8" data-testid="bourse-application-form">
      <div className="border border-gold/30 bg-gold/10 p-5 text-sm leading-relaxed text-foreground/70" data-testid="bourse-campagne-info">
        <strong className="text-foreground">{campagne.titre}.</strong> Campagne ouverte
        {campagne.date_fin ? ` jusqu'au ${new Date(`${campagne.date_fin}T12:00:00`).toLocaleDateString("fr-CA", { dateStyle: "long" })}` : " pour une durée limitée"}.
      </div>

      <section>
        <h3 className="font-serif text-2xl font-semibold">1. Type de demandeur</h3>
        <div className="mt-5 grid gap-3 lg:grid-cols-3">
          {[
            ["personne", "Personne entrepreneure"],
            ["obnl", "Organisme à but non lucratif"],
            ["societe", "Société par actions en démarrage"],
          ].map(([value, label]) => (
            <label key={value} className={`flex cursor-pointer items-center gap-3 border p-5 text-sm font-semibold transition-colors ${form.type_demandeur === value ? "border-primary bg-primary/5 text-primary" : "border-border"}`} data-testid={`bourse-type-${value}`}>
              <input type="radio" name="type_demandeur" value={value} checked={form.type_demandeur === value} onChange={set("type_demandeur")} />
              {label}
            </label>
          ))}
        </div>
      </section>

      <section>
        <h3 className="font-serif text-2xl font-semibold">2. Coordonnées du demandeur ou contact principal</h3>
        <div className="mt-5 grid gap-5 md:grid-cols-2">
          <div><label className={labelCls}>Prénom *</label><input required value={form.prenom} onChange={set("prenom")} className={`${inputCls} mt-2`} data-testid="bourse-prenom" /></div>
          <div><label className={labelCls}>Nom *</label><input required value={form.nom} onChange={set("nom")} className={`${inputCls} mt-2`} data-testid="bourse-nom" /></div>
          <div><label className={labelCls}>Courriel *</label><input required type="email" value={form.courriel} onChange={set("courriel")} className={`${inputCls} mt-2`} data-testid="bourse-courriel" /></div>
          <div><label className={labelCls}>Téléphone *</label><input required type="tel" value={form.telephone} onChange={set("telephone")} className={`${inputCls} mt-2`} data-testid="bourse-telephone" /></div>
          {estOrganisation && <div className="md:col-span-2"><label className={labelCls}>{nomOrganisation}</label><input required value={form.organisation} onChange={set("organisation")} className={`${inputCls} mt-2`} data-testid="bourse-organisation" /></div>}
        </div>
      </section>

      {estOrganisation && (
        <>
          <section data-testid="bourse-representant-section">
            <h3 className="font-serif text-2xl font-semibold">3. Représentant autorisé</h3>
            <div className="mt-5 grid gap-5 md:grid-cols-2">
              <div><label className={labelCls}>Prénom *</label><input required value={representant.prenom} onChange={setRepresentantField("prenom")} className={`${inputCls} mt-2`} data-testid="bourse-rep-prenom" /></div>
              <div><label className={labelCls}>Nom *</label><input required value={representant.nom} onChange={setRepresentantField("nom")} className={`${inputCls} mt-2`} data-testid="bourse-rep-nom" /></div>
              <div><label className={labelCls}>Titre ou fonction *</label><input required value={representant.titre} onChange={setRepresentantField("titre")} className={`${inputCls} mt-2`} data-testid="bourse-rep-titre" /></div>
              <div><label className={labelCls}>Courriel *</label><input required type="email" value={representant.courriel} onChange={setRepresentantField("courriel")} className={`${inputCls} mt-2`} data-testid="bourse-rep-courriel" /></div>
              <div><label className={labelCls}>Téléphone *</label><input required type="tel" value={representant.telephone} onChange={setRepresentantField("telephone")} className={`${inputCls} mt-2`} data-testid="bourse-rep-telephone" /></div>
            </div>
          </section>

          <section data-testid="bourse-ca-section">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className="font-serif text-2xl font-semibold">4. Conseil d'administration</h3>
                <p className="mt-2 text-sm text-foreground/55">Nom, rôle, date de naissance et pièce d'identité gouvernementale pour chaque membre. Maximum 10 membres.</p>
              </div>
              <button type="button" onClick={() => membres.length < 10 && setMembres((m) => [...m, { ...MEMBRE_INITIAL }])} disabled={membres.length >= 10} className="flex items-center gap-2 rounded-sm border border-border px-4 py-2 text-xs font-semibold disabled:opacity-50" data-testid="bourse-add-member">
                <Plus size={14} /> Ajouter un membre
              </button>
            </div>
            <div className="mt-5 space-y-4">
              {membres.map((membre, index) => (
                <div key={index} className="border border-border bg-card p-5" data-testid={`bourse-member-${index}`}>
                  <div className="mb-4 flex items-center justify-between">
                    <p className="text-xs font-semibold uppercase tracking-[0.15em] text-primary">Membre {index + 1}</p>
                    {membres.length > 1 && <button type="button" onClick={() => setMembres((items) => items.filter((_, i) => i !== index))} className="text-destructive" aria-label="Retirer ce membre" data-testid={`bourse-remove-member-${index}`}><Trash2 size={15} /></button>}
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <input required placeholder="Prénom" value={membre.prenom} onChange={(e) => setMembreField(index, "prenom", e.target.value)} className={inputCls} data-testid={`bourse-member-prenom-${index}`} />
                    <input required placeholder="Nom" value={membre.nom} onChange={(e) => setMembreField(index, "nom", e.target.value)} className={inputCls} data-testid={`bourse-member-nom-${index}`} />
                    <input required placeholder="Rôle au CA" value={membre.role} onChange={(e) => setMembreField(index, "role", e.target.value)} className={inputCls} data-testid={`bourse-member-role-${index}`} />
                    <div><label className={labelCls}>Date de naissance *</label><input required type="date" value={membre.date_naissance} onChange={(e) => setMembreField(index, "date_naissance", e.target.value)} className={`${inputCls} mt-2`} data-testid={`bourse-member-naissance-${index}`} /></div>
                    <PieceIdentiteUpload membre={membre} index={index} chargement={chargementDocument === `piece-${index}`} onChange={choisirPieceIdentite} />
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}

      <section>
        <h3 className="font-serif text-2xl font-semibold">{estOrganisation ? "5" : "3"}. Projet et besoin</h3>
        <div className="mt-5 space-y-5">
          <div><label className={labelCls}>Titre du projet *</label><input required value={form.titre_projet} onChange={set("titre_projet")} className={`${inputCls} mt-2`} data-testid="bourse-titre-projet" /></div>
          <div><label className={labelCls}>Description du projet *</label><textarea required rows={5} value={form.description_projet} onChange={set("description_projet")} className={`${inputCls} mt-2 resize-y`} data-testid="bourse-description" /></div>
          <div><label className={labelCls}>Besoin financier et contexte *</label><textarea required rows={4} value={form.besoin_financier} onChange={set("besoin_financier")} className={`${inputCls} mt-2 resize-y`} data-testid="bourse-besoin" /></div>
          <div className="grid gap-5 md:grid-cols-2">
            <div><label className={labelCls}>Montant demandé (CAD) *</label><input required min="1" step="0.01" type="number" value={form.montant_demande} onChange={set("montant_demande")} className={`${inputCls} mt-2`} data-testid="bourse-montant" /></div>
            <div><label className={labelCls}>Chiffre d'affaires réalisé, si applicable</label><input value={form.chiffre_affaires_realise} onChange={set("chiffre_affaires_realise")} className={`${inputCls} mt-2`} data-testid="bourse-chiffre-affaires" /></div>
          </div>
          <div><label className={labelCls}>Impact attendu *</label><textarea required rows={4} value={form.impact_attendu} onChange={set("impact_attendu")} className={`${inputCls} mt-2 resize-y`} data-testid="bourse-impact" /></div>
          <div><label className={labelCls}>Utilisation prévue des fonds *</label><textarea required rows={4} value={form.utilisation_fonds} onChange={set("utilisation_fonds")} className={`${inputCls} mt-2 resize-y`} data-testid="bourse-utilisation" /></div>
        </div>
      </section>

      <section>
        <h3 className="font-serif text-2xl font-semibold">{estOrganisation ? "6" : "4"}. Documents d'analyse</h3>
        <p className="mt-3 text-sm text-foreground/60">Les quatre premiers documents sont obligatoires. Le justificatif de chiffre d'affaires est requis seulement si applicable.</p>
        <div className="mt-5 grid gap-4 md:grid-cols-2">
          {DOCUMENTS.map((doc) => <DocumentUpload key={doc.key} doc={doc} valeur={documents[doc.key]} chargement={chargementDocument === doc.key} onChange={choisirDocument} />)}
        </div>
      </section>

      <label className="flex items-start gap-3 border border-border bg-card p-5 text-sm leading-relaxed text-foreground/70" data-testid="bourse-consent-label">
        <input type="checkbox" checked={form.consentement} onChange={set("consentement")} className="mt-1 accent-primary" data-testid="bourse-consentement" />
        Je consens à ce que la Fondation HORA analyse les renseignements et documents fournis, y compris ceux du représentant autorisé et des membres du conseil d'administration lorsque la demande vient d'une personne morale. Je confirme être autorisé à transmettre ces renseignements.
      </label>

      {confirmation && <p className="border border-primary/30 bg-primary/10 p-4 text-sm font-semibold text-primary" data-testid="bourse-success-message">{confirmation}</p>}

      <button type="submit" disabled={envoi || chargementDocument !== null} className="rounded-sm bg-primary px-9 py-4 text-sm font-semibold text-primary-foreground transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-[hsl(22,83%,40%)] disabled:opacity-60" data-testid="bourse-submit-button">
        {envoi ? "Transmission…" : "Soumettre la demande de bourse"}
      </button>
    </form>
  );
};
